import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../store/slices/settingsSlice';
import { fetchRoles } from '../../store/slices/rolesSlice';
import { fetchTrades } from '../../store/slices/tradesSlice';
import Dialog from '../common/Dialog';

const EditUserDialog = ({ user, isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const { roles, loading: rolesLoading } = useSelector(state => state.roles);
  const trades = useSelector(state => state.trades.items);
  const [tradeSearch, setTradeSearch] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    profile: {
      firstName: '',
      lastName: '',
      phone: '',
      role: '',
      trades: [],
      company: {
        name: '',
        position: '',
        department: ''
      }
    }
  });

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white transition-colors
  `;

  const selectClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white transition-colors
  `;

  const disabledClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    bg-gray-100 text-gray-700 cursor-not-allowed
  `;

  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

  const isSuperAdmin = currentUser?.profile?.role === 'super_admin';
  const isAdmin = currentUser?.profile?.role === 'admin' || isSuperAdmin;
  const isProjectManager = currentUser?.profile?.role === 'project_manager';

  // Determine if user can edit this profile
  const canEdit = isSuperAdmin || 
    (isAdmin && user?.profile?.role !== 'super_admin' && user?.profile?.role !== 'admin') ||
    (isProjectManager && (user?.profile?.role === 'crew_leader' || user?.profile?.role === 'crew_member'));

  // Crew roles are no longer supported via settings; keep the user's existing role
  // as a fallback option so editing pre-existing crew users doesn't blank out their role.
  const HIDDEN_ROLES = ['crew_leader', 'crew_member'];
  const visibleRoles = (roles || []).filter(role =>
    !HIDDEN_ROLES.includes(role.value) || role.value === formData.profile.role
  );

  useEffect(() => {
    if (isOpen) dispatch(fetchTrades());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        profile: {
          firstName: user.profile.firstName,
          lastName: user.profile.lastName,
          phone: user.profile.phone || '',
          role: user.profile.role,
          trades: (user.profile?.trades || []).map((t) => String(t?._id || t)),
          company: {
            name: user.profile.company?.name || '',
            position: user.profile.company?.position || '',
            department: user.profile.company?.department || ''
          }
        }
      });
      setTradeSearch('');
    }
  }, [user]);

  const toggleTrade = (id) => {
    setFormData((prev) => {
      const current = prev.profile.trades || [];
      const has = current.includes(id);
      return {
        ...prev,
        profile: {
          ...prev.profile,
          trades: has ? current.filter((t) => t !== id) : [...current, id]
        }
      };
    });
  };

  const filteredTrades = useMemo(() => {
    const q = tradeSearch.trim().toLowerCase();
    if (!q) return trades;
    return (trades || []).filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [trades, tradeSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          ...(subChild 
            ? {
                [child]: {
                  ...prev[parent][child],
                  [subChild]: value
                }
              }
            : { [child]: value }
          )
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateUser({ id: user._id, updates: formData })).unwrap();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            {canEdit ? 'Edit User' : 'View User'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={labelClasses}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              readOnly={!canEdit}
              className={canEdit ? inputClasses : disabledClasses}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>First Name</label>
              <input
                type="text"
                name="profile.firstName"
                value={formData.profile.firstName}
                onChange={handleChange}
                readOnly={!canEdit}
                className={canEdit ? inputClasses : disabledClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Last Name</label>
              <input
                type="text"
                name="profile.lastName"
                value={formData.profile.lastName}
                onChange={handleChange}
                readOnly={!canEdit}
                className={canEdit ? inputClasses : disabledClasses}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses}>Phone</label>
            <input
              type="tel"
              name="profile.phone"
              value={formData.profile.phone}
              onChange={handleChange}
              readOnly={!canEdit}
              className={canEdit ? inputClasses : disabledClasses}
            />
          </div>

          <div>
            <label className={labelClasses}>Role</label>
            {isSuperAdmin ? (
              <select
                name="profile.role"
                value={formData.profile.role}
                onChange={handleChange}
                className={selectClasses}
              >
                {visibleRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className={disabledClasses}>
                {roles.find(r => r.value === formData.profile.role)?.label || formData.profile.role}
              </div>
            )}
          </div>

          <div>
            <label className={labelClasses}>Trades</label>
            {!canEdit ? (
              <div className={disabledClasses}>
                {(formData.profile.trades || []).length === 0
                  ? 'No trades assigned'
                  : (trades || [])
                    .filter((t) => formData.profile.trades.includes(String(t._id)))
                    .map((t) => t.name)
                    .join(', ')}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search trades…"
                  value={tradeSearch}
                  onChange={(e) => setTradeSearch(e.target.value)}
                  className={inputClasses}
                />
                <div className="mt-2 max-h-44 overflow-y-auto border border-gray-200 rounded-md p-2 bg-white">
                  {(trades || []).length === 0 && (
                    <div className="text-sm text-gray-400 px-1 py-2">
                      No trades available. Super admins can add trades from the calendar's Manage Crews dialog.
                    </div>
                  )}
                  {(trades || []).length > 0 && filteredTrades.length === 0 && (
                    <div className="text-sm text-gray-400 px-1 py-2">No trades match "{tradeSearch}"</div>
                  )}
                  {filteredTrades.map((t) => {
                    const id = String(t._id);
                    const checked = (formData.profile.trades || []).includes(id);
                    return (
                      <label
                        key={id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTrade(id)}
                        />
                        <span className="text-sm text-gray-800">{t.name}</span>
                      </label>
                    );
                  })}
                </div>
                {(formData.profile.trades || []).length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.profile.trades.length} trade{formData.profile.trades.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Company Details</h3>
            <div>
              <label className={labelClasses}>Company Name</label>
              <input
                type="text"
                name="profile.company.name"
                value={formData.profile.company.name}
                onChange={handleChange}
                readOnly={!canEdit}
                className={canEdit ? inputClasses : disabledClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Position</label>
              <input
                type="text"
                name="profile.company.position"
                value={formData.profile.company.position}
                onChange={handleChange}
                readOnly={!canEdit}
                className={canEdit ? inputClasses : disabledClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Department</label>
              <input
                type="text"
                name="profile.company.department"
                value={formData.profile.company.department}
                onChange={handleChange}
                readOnly={!canEdit}
                className={canEdit ? inputClasses : disabledClasses}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            {canEdit && (
              <button
                type="submit"
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            )}
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default EditUserDialog; 